import { Link, useRouterState } from '@tanstack/react-router'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { ErrorBoundary } from '../app/ErrorBoundary'
import { logout } from '../bff/client'
import { ROLE_INTERNAL } from '../bff/types'
import { useAuthenticatedSession } from '../auth/session'
import { useT, type MessageKey, type Translate } from '../i18n'
import { Button } from '../ui'
import styles from './AppShell.module.css'
import { LanguageSwitcher } from './LanguageSwitcher'

interface NavEntry {
  readonly to: string
  readonly labelKey: MessageKey
  readonly glyph: string
  readonly requiresUserManagement?: boolean
  /** Platform administration — biller onboarding and gateway mapping. Hidden
   *  from biller users because the API refuses them anyway, and a nav entry
   *  that always leads to a 403 is a worse experience than no nav entry. */
  readonly requiresInternal?: boolean
  /** The inverse: the support form is the biller-facing door to Maviance and
   *  the API refuses internal staff, so they do not get the entry either. */
  readonly requiresBiller?: boolean
}

const NAV: readonly NavEntry[] = [
  { to: '/transactions', labelKey: 'nav.transactions', glyph: '▤' },
  { to: '/users', labelKey: 'nav.users', glyph: '◑', requiresUserManagement: true },
  { to: '/billers', labelKey: 'nav.billers', glyph: '⬢', requiresInternal: true },
  { to: '/gateways', labelKey: 'nav.gateways', glyph: '⇄', requiresInternal: true },
  { to: '/field-exposure', labelKey: 'nav.fieldExposure', glyph: '◍', requiresInternal: true },
  { to: '/support', labelKey: 'nav.support', glyph: '✆', requiresBiller: true },
  { to: '/profile', labelKey: 'nav.profile', glyph: '◉' },
]

/** The role badge shown under the account name. Two roles exist — internal
 *  staff and a biller's own people — and the badge says which you are. */
function roleLabel(roles: readonly string[], t: Translate): string {
  return roles.includes(ROLE_INTERNAL) ? t('role.internal') : t('role.billerUser')
}

export interface AppShellProps {
  readonly children: ReactNode
  /** Rendered in the sidebar above the nav — the biller switcher for internal
   *  users. Injected rather than imported so the shell does not need to know
   *  how the biller list is fetched. */
  readonly aside?: ReactNode
}

export function AppShell({ children, aside }: AppShellProps) {
  const { user, csrfToken, businessTime, canManageUsers } = useAuthenticatedSession()
  const isInternal = user.isInternal
  const t = useT()
  const [signingOut, setSigningOut] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const signOut = () => {
    setSigningOut(true)
    logout(csrfToken)
      .then((url) => {
        // A real top-level navigation: Keycloak's end-session page cannot be
        // fetched, and the BFF answers with a url precisely so the browser
        // performs this itself rather than following a CSRF-able redirect.
        window.location.assign(url)
      })
      .catch(() => {
        // Local state is already gone server-side even if the response was
        // lost; sending the user to the landing page is the honest outcome.
        window.location.assign('/')
      })
  }

  return (
    <div className={styles.layout}>
      <a className="sr-only sr-only-focusable" href="#main">
        {t('app.skipToContent')}
      </a>

      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            S
          </span>
          <span className={styles.brandName}>{t('app.name')}</span>
        </div>

        <div className={styles.account}>
          <p className={styles.accountName}>{user.name}</p>
          <p className={styles.accountEmail}>{user.email}</p>
          <span className={styles.badge}>{roleLabel(user.roles, t)}</span>
        </div>

        {aside && <div className={styles.switcher}>{aside}</div>}

        <nav className={styles.nav} aria-label={t('nav.primary')}>
          <p className={styles.navLabel}>{t('nav.primary')}</p>
          {NAV.filter(
            (entry) =>
              (!entry.requiresUserManagement || canManageUsers) &&
              (!entry.requiresInternal || isInternal) &&
              (!entry.requiresBiller || !isInternal),
          ).map((entry) => {
            const active = pathname.startsWith(entry.to)
            return (
              <Link
                key={entry.to}
                to={entry.to}
                className={[styles.navItem, active ? styles.navItemActive : ''].filter(Boolean).join(' ')}
                // The visual treatment alone does not convey "current" to a
                // screen reader; aria-current does.
                aria-current={active ? 'page' : undefined}
              >
                <span className={styles.navGlyph} aria-hidden="true">
                  {entry.glyph}
                </span>
                {t(entry.labelKey)}
              </Link>
            )
          })}
        </nav>

        <div className={styles.footer}>
          <LanguageSwitcher />
          {businessTime && (
            <p className={styles.timezone}>
              {t('shell.timezoneNote', { timezone: businessTime.timezone })}
            </p>
          )}
          {/* Small print like the timezone note: the build this screen runs. */}
          <p className={styles.timezone}>{t('shell.version', { version: import.meta.env.VITE_APP_VERSION ?? 'dev' })}</p>
          <Button variant="secondary" size="sm" fullWidth onClick={signOut} loading={signingOut}>
            {signingOut ? t('shell.signingOut') : t('shell.signOut')}
          </Button>
        </div>
      </aside>

      <div className={styles.main}>
        <main className={styles.content} id="main" tabIndex={-1}>
          {/* Scoped to the content area: a screen that throws must not take
              the navigation and sign-out control down with it. */}
          <ErrorBoundary area="screen">{children}</ErrorBoundary>
        </main>
      </div>
    </div>
  )
}
